import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useCart } from '../context/CartContext';
import { useSearch } from '../App';
import axios from 'axios';
import '../stylings/BucksShop.css';
import BucksCustomerProductModalDetails from './BucksCustomerProductModalDetails';

// Optimized Image Component with lazy loading
const LazyImage = React.memo(({ src, alt, className, onLoad }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [imageRef, setImageRef] = useState();
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let observer;
    
    if (imageRef && imageSrc !== src) {
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setImageSrc(src);
              observer.unobserve(imageRef);
            }
          });
        },
        { threshold: 0.1, rootMargin: '50px' }
      );
      observer.observe(imageRef);
    }
    
    return () => {
      if (observer && observer.unobserve) {
        observer.disconnect();
      }
    };
  }, [imageRef, src, imageSrc]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoaded(true);
  }, []);

  return (
    <div 
      ref={setImageRef} 
      className={`${className} ${isLoaded ? 'loaded' : 'loading'}`}
      style={{ 
        backgroundColor: hasError ? '#f5f5f5' : '#f0f0f0',
        minHeight: '200px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {imageSrc && !hasError && (
        <img
          src={imageSrc}
          alt={alt}
          className={className}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}
      {!isLoaded && !hasError && (
        <div className="image-loading-placeholder">
          <div className="loading-spinner-small"></div>
        </div>
      )}
      {hasError && (
        <div className="image-error-placeholder">
          <span>📷</span>
          <small>Image unavailable</small>
        </div>
      )}
    </div>
  );
});

function BucksShop() {
  // State management
  const [activeCategory, setActiveCategory] = useState('all');
  const [isVisible, setIsVisible] = useState(false);
  const [featuredProduct, setFeaturedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasNext: false,
    hasPrev: false
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasNetworkError, setHasNetworkError] = useState(false);
  
  // Add these new state variables for quantity input functionality
  const [quantityInputMode, setQuantityInputMode] = useState({}); // Track which products are in input mode
  const [quantityValues, setQuantityValues] = useState({}); // Track quantity values for each product
  
  // Context hooks - extract specific values to avoid reference issues
  const { addToCart, refreshCart, validationMessage } = useCart();
  const searchContext = useSearch();
  
  // Extract search context values to stable references
  const searchQuery = searchContext?.searchQuery || '';
  const searchResults = searchContext?.searchResults || [];
  const setSearchQuery = searchContext?.setSearchQuery;
  const setSearchResults = searchContext?.setSearchResults;
  
  // API endpoint
  const API_ENDPOINT = 'https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-inventory-product-get';

  // Stable notification function - no dependencies
  const showNotification = useCallback((message, type = 'success') => {
    const notification = document.getElementById('cart-notification');
    if (notification) {
      notification.textContent = message;
      notification.classList.add('show');
      if (type === 'error') {
        notification.classList.add('error');
      }
      
      setTimeout(() => {
        notification.classList.remove('show', 'error');
      }, type === 'error' ? 5000 : 3000);
    }
  }, []); // Empty dependency array - function is stable

  // Memoized fetch function - now with stable dependencies
  const fetchProducts = useCallback(async (category = 'all', page = 1, search = '') => {
    setIsLoading(true);
    setHasNetworkError(false);
    try {
      const params = {
        page: page,
        page_size: 12,
        include_images: true
      };
      
      if (category && category !== 'all') {
        params.category = category;
      }
      
      if (search) {
        params.search = search;
      }
      
      const response = await axios.get(API_ENDPOINT, { 
        params,
        timeout: 15000
      });
      
      if (response.data && response.data.products) {
        setProducts(response.data.products);
        if (response.data.categories) {
          setCategories(response.data.categories);
        }
        setPagination({
          page: response.data.pagination?.page || 1,
          totalPages: response.data.pagination?.total_pages || 1,
          hasNext: response.data.pagination?.has_next || false,
          hasPrev: response.data.pagination?.has_prev || false
        });
      }
      return response;
    } catch (error) {
      console.error('Error fetching products:', error);
      
      if (error.message === 'Network Error') {
        console.error('CORS or network connectivity issue detected');
        setHasNetworkError(true);
        showNotification('Unable to connect to the server. Please check your connection.', 'error');
      } else if (error.response) {
        console.error('Server error:', error.response.status, error.response.data);
        showNotification(`Server error: ${error.response.status}`, 'error');
      } else if (error.request) {
        console.error('No response received:', error.request);
        setHasNetworkError(true);
        showNotification('No response from server. Please try again.', 'error');
      }
      
      setProducts([]);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [showNotification]); // Only depend on showNotification

  // Memoized fetch single product function
  const fetchProductById = useCallback(async (productId) => {
    try {
      const response = await axios.get(API_ENDPOINT, {
        params: { product_id: productId },
        timeout: 15000
      });
      
      if (response.data && response.data.id) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching product by ID:', error);
      
      if (error.message === 'Network Error') {
        console.error('CORS or network connectivity issue detected');
        showNotification('Unable to load product details. Please check your connection.', 'error');
      }
      
      return null;
    }
  }, [showNotification]); // Only depend on showNotification

  // Initial load effect - fixed dependencies
  useEffect(() => {
    setIsVisible(true);
    refreshCart();
    
    // Handle URL parameters
    const searchParams = new URLSearchParams(window.location.search);
    const productId = searchParams.get('product');
    const categoryParam = searchParams.get('category') || 'all';
    const pageParam = parseInt(searchParams.get('page') || '1', 10);
    const searchQueryFromUrl = searchParams.get('search') || '';
    const showModal = searchParams.get('showModal') === 'true';
    
    setActiveCategory(categoryParam);
    
    // Handle product modal
    if (productId && showModal) {
      (async () => {
        const product = await fetchProductById(productId);
        if (product) {
          setFeaturedProduct(product);
          setSelectedProduct(product);
          setIsModalOpen(true);
          
          setTimeout(() => {
            const element = document.getElementById(`product-${productId}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.classList.add('highlighted');
              setTimeout(() => element.classList.remove('highlighted'), 3000);
            }
          }, 500);
        } else {
          const url = new URL(window.location);
          url.searchParams.delete('product');
          url.searchParams.delete('showModal');
          window.history.pushState({}, '', url);
          
          showNotification("The requested product is no longer available.", 'error');
        }
      })();
    } else {
      setFeaturedProduct(null);
      if (productId && !showModal) {
        const url = new URL(window.location);
        url.searchParams.delete('product');
        window.history.pushState({}, '', url);
      }
    }
    
    // Load products
    fetchProducts(categoryParam, pageParam, searchQueryFromUrl);
    
    // Handle search context
    if (searchQuery && searchResults.length > 0) {
      const productResults = searchResults.filter(result => 
        result.type === 'product'
      );
      
      if (productResults.length > 0) {
        const firstResult = productResults[0];
        (async () => {
          const product = await fetchProductById(firstResult.id);
          if (product) {
            setFeaturedProduct(product);
          }
        })();
      }
    }
  }, []); // Empty dependency array - run only once on mount

  // Separate effect for search query changes
  useEffect(() => {
    if (searchQuery) {
      fetchProducts(activeCategory, 1, searchQuery);
    }
  }, [searchQuery, activeCategory, fetchProducts]);

  // Memoized category change handler
  const handleCategoryChange = useCallback((category) => {
    setActiveCategory(category);
    fetchProducts(category, 1, searchQuery);
    
    const url = new URL(window.location);
    url.searchParams.set('category', category);
    url.searchParams.set('page', '1');
    window.history.pushState({}, '', url);
  }, [fetchProducts, searchQuery]);
  
  // Memoized pagination handler
  const handlePageChange = useCallback((newPage) => {
    setIsPaginationLoading(true);
    
    fetchProducts(activeCategory, newPage, searchQuery)
      .finally(() => {
        setIsPaginationLoading(false);
        
        const productGridSection = document.querySelector('.product-categories');
        if (productGridSection) {
          productGridSection.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    
    const url = new URL(window.location);
    url.searchParams.set('page', newPage.toString());
    window.history.pushState({}, '', url);
  }, [activeCategory, fetchProducts, searchQuery]);

  // Add this helper function for special pricing calculation
  const calculateSpecialPricing = useCallback((product, quantity) => {
    // Special pricing for sweet corn - dozen pricing
    if (product.name && product.name.toLowerCase().includes('corn') && quantity >= 12) {
      const dozens = Math.floor(quantity / 12);
      const remainder = quantity % 12;
      const dozenPrice = 8.00; // $8 per dozen
      const singlePrice = product.price; // Original unit price
      
      return {
        totalPrice: (dozens * dozenPrice) + (remainder * singlePrice),
        effectiveUnitPrice: (dozens * dozenPrice + remainder * singlePrice) / quantity,
        hasSpecialPricing: true,
        dozens: dozens,
        remainder: remainder
      };
    }
    
    return {
      totalPrice: product.price * quantity,
      effectiveUnitPrice: product.price,
      hasSpecialPricing: false
    };
  }, []);

  // Enhanced add to cart handler with quantity validation, special pricing and consistent product structure
  const handleAddToCart = useCallback((product, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    // If not in quantity input mode, show the input
    if (!quantityInputMode[product.id]) {
      setQuantityInputMode(prev => ({
        ...prev,
        [product.id]: true
      }));
      setQuantityValues(prev => ({
        ...prev,
        [product.id]: 1 // Default to 1
      }));
      return;
    }
    
    // Ensure we have a valid quantity before adding to cart
    let quantity = quantityValues[product.id];
    if (quantity === '' || quantity === null || quantity === undefined || quantity < 1) {
      quantity = 1;
      // Update the state to reflect the corrected value
      setQuantityValues(prev => ({
        ...prev,
        [product.id]: 1
      }));
    }
    
    const specialPricing = calculateSpecialPricing(product, quantity);
    
    // Ensure consistent product structure - normalize the product  
    const cartProduct = { 
      // Use a consistent ID format - ensure numeric ID
      id: typeof product.id === 'string' ? parseInt(product.id, 10) || product.id : product.id,
      // Ensure consistent name formatting
      name: product.name.trim(),
      price: product.price,
      image: product.image,
      category: product.category,
      description: product.description,
      status: product.status,
      // Cart-specific fields
      quantity: quantity,
      unit: product.unit || 'ea',
      effectiveUnitPrice: specialPricing.effectiveUnitPrice,
      originalPrice: product.price,
      hasSpecialPricing: specialPricing.hasSpecialPricing,
      specialPricingInfo: specialPricing.hasSpecialPricing ? {
        dozens: specialPricing.dozens,
        remainder: specialPricing.remainder,
        dozenPrice: 8.00,
        originalUnitPrice: product.price,
        totalSavings: (quantity * product.price) - specialPricing.totalPrice
      } : null
    };
    
    console.log('Cart product being added from shop page:', cartProduct);
    
    addToCart(cartProduct);
    
    // Enhanced notification message for special pricing
    let notificationMessage;
    if (specialPricing.hasSpecialPricing && specialPricing.dozens > 0) {
      if (specialPricing.remainder > 0) {
        notificationMessage = `${specialPricing.dozens} dozen + ${specialPricing.remainder} ${product.name} added to basket (${specialPricing.totalPrice.toFixed(2)})`;
      } else {
        notificationMessage = `${specialPricing.dozens} dozen ${product.name} added to basket (${specialPricing.totalPrice.toFixed(2)})`;
      }
    } else {
      // Updated: Include unit for lbs, omit for other units
      const unit = product.unit || 'ea';
      if (unit.toLowerCase() === 'lbs' || unit.toLowerCase() === 'lb') {
        notificationMessage = `${quantity} ${unit} of ${product.name} added to basket`;
      } else {
        notificationMessage = `${quantity} ${product.name} added to basket`;
      }
    }
    
    showNotification(notificationMessage);
    
    // Reset the input mode and value
    setQuantityInputMode(prev => ({
      ...prev,
      [product.id]: false
    }));
    setQuantityValues(prev => ({
      ...prev,
      [product.id]: 1
    }));
  }, [addToCart, showNotification, quantityInputMode, quantityValues, calculateSpecialPricing]);

  // Enhanced quantity change handler that allows empty strings temporarily
  const handleQuantityChange = useCallback((productId, value) => {
    // Allow empty string temporarily for better UX
    if (value === '') {
      setQuantityValues(prev => ({
        ...prev,
        [productId]: ''
      }));
      return;
    }
    
    // Parse the value and ensure it's a positive number
    const numValue = parseInt(value, 10);
    
    // Only update if it's a valid positive number
    if (!isNaN(numValue) && numValue > 0) {
      setQuantityValues(prev => ({
        ...prev,
        [productId]: numValue
      }));
    }
  }, []);

  // Add this new function to handle when user leaves the input field
  const handleQuantityBlur = useCallback((productId) => {
    const currentValue = quantityValues[productId];
    
    // If empty or invalid, set to 1
    if (currentValue === '' || currentValue === null || currentValue === undefined || currentValue < 1) {
      setQuantityValues(prev => ({
        ...prev,
        [productId]: 1
      }));
    }
  }, [quantityValues]);

  // Add this function to handle input focus (select all text)
  const handleQuantityFocus = useCallback((event) => {
    // Select all text when input is focused for easier editing
    event.target.select();
  }, []);



  // Add this function to cancel quantity input mode
  const handleCancelQuantityInput = useCallback((productId, event) => {
    if (event) {
      event.stopPropagation();
    }
    setQuantityInputMode(prev => ({
      ...prev,
      [productId]: false
    }));
    setQuantityValues(prev => ({
      ...prev,
      [productId]: 1
    }));
  }, []);

  // Memoized product click handler
  const handleProductClick = useCallback((product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
    
    const url = new URL(window.location);
    url.searchParams.set('product', product.id.toString());
    url.searchParams.set('showModal', 'true');
    window.history.pushState({}, '', url);
  }, []);
  
  // Memoized modal close handler
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setFeaturedProduct(null);
    
    const url = new URL(window.location);
    url.searchParams.delete('showModal');
    url.searchParams.delete('product');
    window.history.pushState({}, '', url);
  }, []);

  // Memoized status class function
  const getStatusClass = useCallback((status) => {
    if (!status) return 'in-stock';
    
    switch(status.toLowerCase()) {
      case 'out of stock':
        return 'out-of-stock';
      case 'low stock':
        return 'low-stock';
      default:
        return 'in-stock';
    }
  }, []);

  // Memoized components
  const renderFeaturedProduct = useMemo(() => {
    if (!featuredProduct) return null;
    
    return (
      <section className="featured-product-section">
        <div className="featured-product-container">
          <div className="featured-product-image">
            <LazyImage 
              src={featuredProduct.image} 
              alt={featuredProduct.name}
              className="featured-product-img"
            />
          </div>
          <div className="featured-product-details">
            <h2>{featuredProduct.name}</h2>
            <p className="featured-product-category">{featuredProduct.category}</p>
            <p className="featured-product-price">
              ${featuredProduct.price.toFixed(2)} {featuredProduct.unit && `/ ${featuredProduct.unit}`}
            </p>
            <p className="featured-product-description">{featuredProduct.description}</p>
            <div className={`featured-product-status ${getStatusClass(featuredProduct.status)}`}>
              {featuredProduct.status || 'In Stock'}
            </div>
            {featuredProduct.is_out_of_season && (
              <div className="featured-product-season-badge">
                Out of Season
              </div>
            )}
            <button 
              className="featured-add-to-cart-btn" 
              onClick={(e) => handleAddToCart(featuredProduct, e)}
              disabled={featuredProduct.status === 'Out of Stock'}
            >
              {featuredProduct.status === 'Out of Stock' ? 'Out of Stock' : 'Add to Basket'}
            </button>
            <button 
              className="featured-view-details-btn"
              onClick={() => handleProductClick(featuredProduct)}
            >
              View Details
            </button>
          </div>
        </div>
      </section>
    );
  }, [featuredProduct, getStatusClass, handleAddToCart, handleProductClick]);

  const renderSearchResultsMessage = useMemo(() => {
    if (searchQuery) {
      return (
        <div className="search-results-message">
          <p>Showing results for "{searchQuery}"</p>
          <button 
            className="clear-search-btn"
            onClick={() => {
              if (setSearchQuery) {
                setSearchQuery('');
              }
              if (setSearchResults) {
                setSearchResults([]);
              }
              setFeaturedProduct(null);
              fetchProducts(activeCategory, 1);
            }}
          >
            Clear Search
          </button>
        </div>
      );
    }
    return null;
  }, [searchQuery, setSearchQuery, setSearchResults, fetchProducts, activeCategory]);
  
  const renderPagination = useMemo(() => {
    if (!pagination || pagination.totalPages <= 1) return null;
    
    return (
      <div className="pagination-controls">
        <button 
          className="pagination-btn prev"
          disabled={!pagination.hasPrev || isPaginationLoading}
          onClick={() => handlePageChange(pagination.page - 1)}
        >
          {isPaginationLoading ? 'Loading...' : 'Previous'}
        </button>
        <span className="pagination-info">
          Page {pagination.page} of {pagination.totalPages}
        </span>
        <button 
          className="pagination-btn next"
          disabled={!pagination.hasNext || isPaginationLoading}
          onClick={() => handlePageChange(pagination.page + 1)}
        >
          {isPaginationLoading ? 'Loading...' : 'Next'}
        </button>
      </div>
    );
  }, [pagination, isPaginationLoading, handlePageChange]);

  const renderEmptyState = useMemo(() => {
    const isFiltering = searchQuery || (activeCategory !== 'all');
    
    return (
      <div className="empty-state-container">
        <div className="empty-state-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="120" height="120">
            <path d="M64 96H0c0 123.7 100.3 224 224 224v144c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V320C288 196.3 187.7 96 64 96zm384-64c-84.2 0-157.4 46.5-195.7 115.2 27.7 30.2 48.2 66.9 59 107.6C424 243.1 512 147.9 512 32h-64z" fill="#a67c52" opacity="0.2" />
            <path d="M64 96H0c0 123.7 100.3 224 224 224v144c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V320C288 196.3 187.7 96 64 96zm384-64c-84.2 0-157.4 46.5-195.7 115.2 27.7 30.2 48.2 66.9 59 107.6C424 243.1 512 147.9 512 32h-64z" fill="#a67c52" />
          </svg>
        </div>
        {isFiltering ? (
          <>
            <h2 className="empty-state-title">The Harvest Isn't In Yet</h2>
            <p className="empty-state-message">
              We couldn't find any products matching your current selection. Our hardworking farmers haven't harvested these items yet!
            </p>
            <div className="empty-state-actions">
              <button 
                className="empty-state-button"
                onClick={() => {
                  if (setSearchQuery) {
                    setSearchQuery('');
                  }
                  if (setSearchResults) {
                    setSearchResults([]);
                  }
                  setActiveCategory('all');
                  fetchProducts('all', 1, '');
                  
                  const url = new URL(window.location);
                  url.searchParams.delete('search');
                  url.searchParams.set('category', 'all');
                  url.searchParams.set('page', '1');
                  window.history.pushState({}, '', url);
                }}
              >
                View All Available Products
              </button>
            </div>
          </>
        ) : (
          <>
           <h2 className="empty-state-title">Our Farm Stand is Restocking</h2>
            <p className="empty-state-message">
              We're busy restocking our shelves with fresh, seasonal produce from our farm. Please check back soon to see what delicious goodies we've added.
            </p>
            <div className="empty-state-subtitle">
              In the meantime, reach out with any questions about upcoming harvests!
            </div>
          </>
        )}
      </div>
    );
  }, [searchQuery, activeCategory, setSearchQuery, setSearchResults, fetchProducts]);

  // Loading state
  if (isLoading && products.length === 0) {
    return (
      <div className="bucks-shop">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  // Network error state
  if (hasNetworkError && products.length === 0) {
    return (
      <div className="bucks-shop">
        <section className="shop-hero visible">
          <div className="shop-hero-content">
            <h1>Connection Issue</h1>
            <p className="shop-hero-subtitle">We're having trouble connecting to our servers.</p>
          </div>
        </section>
        <div className="empty-state-container" style={{ marginTop: '2rem' }}>
          <div className="empty-state-icon">
            <span style={{ fontSize: '4rem' }}>🔌</span>
          </div>
          <h2 className="empty-state-title">Unable to Load Products</h2>
          <p className="empty-state-message">
            We're experiencing connection issues. This could be due to:
          </p>
          <ul style={{ textAlign: 'left', maxWidth: '400px', margin: '0 auto 1.5rem' }}>
            <li>Your internet connection</li>
            <li>Our servers being temporarily unavailable</li>
            <li>Browser security settings blocking the connection</li>
          </ul>
          <div className="empty-state-actions">
            <button 
              className="empty-state-button"
              onClick={() => {
                setHasNetworkError(false);
                fetchProducts(activeCategory, pagination.page, searchQuery);
              }}
            >
              Try Again
            </button>
          </div>
          <div className="empty-state-subtitle" style={{ marginTop: '1rem' }}>
            If the problem persists, please try refreshing the page or contact support.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bucks-shop">
      {/* Shop Hero Section */}
      <section className={`shop-hero ${isVisible ? 'visible' : ''}`}>
        <div className="shop-hero-content">
          <h1>Farm Fresh Goodness</h1>
          <p className="shop-hero-subtitle">Explore our selection of freshly harvested produce and handcrafted goods.</p>
        </div>
      </section>

      {/* Cart Validation Message */}
      {validationMessage && (
        <div className="cart-validation-message">
          <p>{validationMessage}</p>
        </div>
      )}

      {/* Featured Product Section */}
      {renderFeaturedProduct}

      {/* Search Results Message */}
      {renderSearchResultsMessage}

      {/* Product Categories Navigation */}
      <section className="product-categories">
        <div className="category-tabs">
          <button 
            className={`category-tab ${activeCategory === 'all' ? 'active' : ''}`}
            onClick={() => handleCategoryChange('all')}
          >
            All Products
          </button>
          {categories && categories.map(category => (
            <button 
              key={category}
              className={`category-tab ${activeCategory === category ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Product Grid */}
      <section className="product-grid-container">
        <div className="product-grid">
          {products.length > 0 ? (
            products.map((product) => (
              <div 
                className={`product-card ${featuredProduct && featuredProduct.id === product.id ? 'featured' : ''} ${product.is_out_of_season ? 'out-of-season' : ''}`} 
                key={product.id}
                id={`product-${product.id}`}
                onClick={() => handleProductClick(product)}
              >
                <div className="product-clickable-indicator">
                  <span>Click for details</span>
                </div>
                
                {product.is_out_of_season && (
                  <div className="product-season-badge">Out of Season</div>
                )}
                
                <div className="product-image-container">
                  <LazyImage 
                    src={product.image} 
                    alt={product.name} 
                    className="product-image"
                  />
                </div>
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-description">{product.description}</p>
                  <div className="product-price-container">
                    <span className="product-price">${product.price.toFixed(2)} {product.unit && `/ ${product.unit}`}</span>
                    
                    {/* Quantity Input Mode */}
                    {quantityInputMode[product.id] ? (
                      <div className="quantity-input-container">
                        <div className="quantity-input-wrapper">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={quantityValues[product.id] === '' ? '' : (quantityValues[product.id] || 1)}
                            onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                            onBlur={() => handleQuantityBlur(product.id)}
                            onFocus={handleQuantityFocus}
                            className="quantity-input"
                            placeholder="1"
                            onClick={(e) => e.stopPropagation()}
                            autoComplete="off"
                          />
                          <span className="quantity-unit">{product.unit || 'ea'}</span>
                        </div>
                        
                        {/* Special Pricing Preview for Corn */}
                        {product.name && product.name.toLowerCase().includes('corn') && (quantityValues[product.id] >= 12) && (
                          <div className="special-pricing-preview">
                            {(() => {
                              const qty = quantityValues[product.id] || 1;
                              // Only show pricing preview if we have a valid number
                              if (qty === '' || qty < 1) return null;
                              
                              const pricing = calculateSpecialPricing(product, qty);
                              const dozens = Math.floor(qty / 12);
                              const remainder = qty % 12;
                              
                              return (
                                <div className="pricing-breakdown">
                                  <div className="dozen-pricing">
                                    🌽 {dozens} dozen @ $8.00 = ${(dozens * 8).toFixed(2)}
                                  </div>
                                  {remainder > 0 && (
                                    <div className="remainder-pricing">
                                      + {remainder} ea @ ${product.price.toFixed(2)} = ${(remainder * product.price).toFixed(2)}
                                    </div>
                                  )}
                                  <div className="total-pricing">
                                    <strong>Total: ${pricing.totalPrice.toFixed(2)}</strong>
                                    <span className="savings"> (Save ${((qty * product.price) - pricing.totalPrice).toFixed(2)}!)</span>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        
                        <div className="quantity-actions">
                          <button 
                            className="confirm-add-btn" 
                            onClick={(e) => handleAddToCart(product, e)}
                            disabled={product.status === 'Out of Stock'}
                          >
                            Add to Basket
                          </button>
                          <button 
                            className="cancel-quantity-btn"
                            onClick={(e) => handleCancelQuantityInput(product.id, e)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Regular Add to Cart Button */
                      <button 
                        className="add-to-cart-btn" 
                        onClick={(e) => handleAddToCart(product, e)}
                        aria-label={`Add ${product.name} to basket`}
                        disabled={product.status === 'Out of Stock'}
                      >
                        {product.status === 'Out of Stock' ? 'Out of Stock' : 'Add to Basket'}
                      </button>
                    )}
                  </div>
                  <div className={`product-status-indicator ${getStatusClass(product.status)}`}>
                    {product.status || 'In Stock'}
                  </div>
                </div>
              </div>
            ))
          ) : (
            renderEmptyState
          )}
        </div>
      </section>
      
      {/* Pagination Controls */}
      {renderPagination}

      {/* Farm Promise Section */}
      <section className="farm-promise">
        <div className="promise-container">
          <h2>Our Buck's Promise</h2>
          <div className="promises">
            <div className="promise-item">
              <div className="promise-icon">🌱</div>
              <h3>Locally Grown</h3>
              <p>All our produce is grown right here on our farm or sourced from local farmers we know and trust.</p>
            </div>
            <div className="promise-item">
              <div className="promise-icon">🌿</div>
              <h3>Sustainable Practices</h3>
              <p>We employ farming methods that protect our land and ensure sustainability for future generations.</p>
            </div>
            <div className="promise-item">
              <div className="promise-icon">🍎</div>
              <h3>Fresh Harvest</h3>
              <p>Our produce is harvested at peak ripeness to ensure maximum flavor and nutritional value.</p>
            </div>
            <div className="promise-item">
              <div className="promise-icon">🚚</div>
              <h3>Farm to Table</h3>
              <p>From our fields to your table in the shortest time possible to preserve freshness.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cart Notification */}
      <div id="cart-notification" className="cart-notification">
        Item added to basket
      </div>

      {/* Product Detail Modal */}
      {isModalOpen && selectedProduct && (
        <BucksCustomerProductModalDetails 
          product={selectedProduct} 
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

export default BucksShop;